<template>
  <div>
    <p v-if="errors.length">
    <b style="color: red">Please correct the following error(s):</b>
    <ul>
      <li v-for="error in errors" :key="error"><span style="color: black"> {{ error }}</span></li>
    </ul>
    </p>
    <div class="form-group">
      <label for="name">Name:</label>
      <input class="form-control" id="name" placeholder="Name" v-model="name"/>
    </div>
    <div class="form-group">
      <label for="description">Description:</label>
      <input
        class="form-control"
        id="description"
        placeholder="Description"
        v-model="description"
      />
    </div>
    <div class="form-group">
      <label for="percentage">Percentage:</label>
      <input
        class="form-control"
        id="percentage"
        placeholder="Percentage"
        v-model.number="percentage"
        type="number"
      />
    </div>
    <p>Conditions:</p>
    <div v-for="(condition, index) in conditions" :key="`condition${index}`">
      <div class="conditionDiv p-4">
        Condition {{ index + 1 }}:
        <div class="row">
          <div class="col-10">
            <div class="row">
              <div class="col-md-4">
                <select
                  v-model="condition.key"
                  class="form-control"
                  @change="keySelected(index)"
                  id="condition-key"
                >
                  <option disabled value="">Please select a type</option>
                  <option
                    v-for="key in keys"
                    :key="`audienceKeySelect-${index}-${key}`"
                    :value="key"
                  >
                    {{ key == "relative_created_at" ? "created_at (relative - in days)" : key }}
                  </option>
                </select>
              </div>
              <div class="col-md-8">
                <div class="row" v-if="conditionTypes[index] && typeof conditionTypes[index] == 'string' && conditionTypes[index].indexOf('custom') >= 0">
                  <div class="col-md-2">
                    <select v-model="condition.operator" class="form-control" id="condition-operator">
                      <option disabled value="">Please select an operator</option>
                      <option
                        v-for="op in operatorTypes(conditionTypes[index])"
                        :key="`audienceOpSelect-${index}-${op}`"
                      >
                        {{ op }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-5">
                    <input
                      class="form-control"
                      placeholder="Custom Key"
                      v-model="condition.custom_key"
                    />
                  </div>
                  <div class="col-md-5">
                    <input
                      class="form-control"
                      placeholder="Value"
                      v-model="condition.value"
                    />
                  </div>
                </div>
                <div class="row" v-else>
                  <div class="col-md-6">
                    <div class="form-group">
                      <select v-model="condition.operator" class="form-control" id="condition-operator" @change="operatorChanged(index)">
                        <option disabled value="">Please select an operator</option>
                        <option
                          v-for="op in operatorTypes(conditionTypes[index], condition.key)"
                          :key="`audienceOpSelect-${index}-${op}`"
                        >
                          {{ op }}
                        </option>
                      </select>
                    </div>
                    <div v-if="isCountryInCondition(index)" class="form-group">
                      <select
                        v-model="condition.countryShortcut"
                        @change="applyCountryShortcut($event, index)"
                        class="form-control"
                      >
                        <option value="">Add from shortcut...</option>
                        <optgroup label="Country Groups">
                          <option
                            v-for="g in countryGroupsClustersSafe.countryGroups"
                            :key="`group-${g.id}`"
                            :value="`group:${g.id}`"
                          >
                            {{ g.label }}
                          </option>
                        </optgroup>
                        <optgroup label="Country Clusters">
                          <option
                            v-for="c in countryGroupsClustersSafe.countryClusters"
                            :key="`cluster-${c.id}`"
                            :value="`cluster:${c.id}`"
                          >
                            {{ c.label }}
                          </option>
                        </optgroup>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <enhanced-autocomplete
                      v-if="conditionTypes[index] == 'autocomplete'"
                      :key="`autocomplete-${index}-${condition.key}-${condition.operator}`"
                      :search="search(index, condition.key)"
                      v-model="condition.value"
                      :is-multi-select="isMultiSelect(index)"
                      :placeholder="isMultiSelect(index) ? 'Add multiple values...' : 'Search...'"
                      :debounceTime="200"
                      :ref="`autocomplete-${index}`"
                      :field-type="condition.key"
                    ></enhanced-autocomplete>
                    
                    <select
                      v-if="conditionTypes[index] == 'objselect'"
                      v-model="condition.value"
                      class="form-control"
                      id="condition-value"
                    >
                      <option disabled value="">Please select a value</option>
                      <option
                        v-for="op in valueOptions(condition.key)"
                        :key="`audienceValueSelect-${index}-${op.id}`"
                        :value="op.id"
                      >
                        {{ op.name }}
                      </option>
                    </select>
                    <select
                      v-if="conditionTypes[index] == 'select'"
                      v-model="condition.value"
                      class="form-control"
                      id="condition-value"
                    >
                      <option disabled value="">Please select a value</option>
                      <option
                        v-for="op in valueOptions(condition.key)"
                        :key="`audienceValueSelect-${index}-${op}`"
                      >
                        {{ op }}
                      </option>
                    </select>
                    <select
                      v-if="conditionTypes[index] == 'codedSelect' || ((condition.key === 'device_region' || condition.key === 'country') && (condition.operator !== 'in'))"
                      v-model="condition.value"
                      class="form-control"
                      id="condition-value"
                    >
                      <option disabled value="">Please select a value</option>
                      <option
                        :value="op.code"
                        v-for="op in valueOptions(condition.key)"
                        :key="`audienceValueSelect-${index}-${op.name}`"
                      >
                        {{ op.name }}
                      </option>
                    </select>
                    <datetime
                      v-if="conditionTypes[index] == 'calendar'"
                      input-class="form-control"
                      type="datetime"
                      v-model="condition.value"
                    ></datetime>
                    <input
                      v-if="isCountryInCondition(index)"
                      class="form-control"
                      placeholder="Value (e.g. US,AU,GB or use shortcut)"
                      v-model="condition.value"
                      @input="onCountryValueInput(index)"
                    />
                    <input
                      v-else-if="
                        (conditionTypes[index] == 'list' && 
                          (condition.key !== 'device_region' && condition.key !== 'country' || 
                          (condition.operator !== 'not' && condition.operator === 'in'))) ||
                        conditionTypes[index] == 'operators'
                      "
                      class="form-control"
                      placeholder="Value"
                      v-model="condition.value"
                    />
                    <input
                      v-if="!conditionTypes[index]"
                      class="form-control"
                      style="background-color:inherit"
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-2">
            <button class="btn btn-sm btn-info" @click="addOrCondition(index)">
              OR
            </button>
            <button class="btn btn-sm btn-danger" @click="removeCondition(index)">
              X
            </button>
          </div>
        </div>
      </div>
      <div v-if="index != conditions.length - 1">
        <p v-if="condition.hasOr" class="text-center or-hr">OR</p>
        <p v-else>AND</p>
      </div>
    </div>
    <button type="button" class="btn btn-sm btn-info" @click="addCondition">
      AND
    </button>
  </div>
</template>

<script>
import Autocomplete from '@trevoreyre/autocomplete-vue'
import EnhancedAutocomplete from './EnhancedAutocomplete'
import axios from 'axios'
import { Datetime } from 'vue-datetime'
import BaseHttpService from '../services/https.service'
/* eslint-disable global-require */
const countryGroupsClusters = require('../../json/CountryGroupsClusters.json')

export default {
  props: ["audience"],
  created(){
    this.$parent.$on('createAudience', this.createAudience)
    this.$parent.$on('editAudience', this.editAudience)
    // put the necessary values if this is an edit view
    if (this.audience){
      this.name = this.audience.name
      this.description = this.audience.description
      this.percentage = this.audience.percentage
      this.conditions = this.audience.conditions
      // convert date integers to iso strings since vue-datetime works with iso strings
      this.conditions.forEach((item, index) => {
        if (item.key == "created_at" || item.key == "update_time") {
          item.value = new Date(item.value).toISOString()
        }
      })
      this.conditionTypes = this.audience.conditions.map((condition, index) => {
        const audOpt = this.audienceOptions.find(item => item.key == this.conditions[index].key)
        if (audOpt != undefined){
          return audOpt.type
        }else {
          return
        }
      })
    }
  },
  mounted(){
    // put in autocomplete values since it does not support v-model for the time being
    if (this.audience){
      this.conditions.forEach((item, index) => {
        if (item.key == "city" || item.key == "region"){
          const ref = `autocomplete-${index}`
          if (this.$refs[ref] && this.$refs[ref][0]) {
            // No need to manually handle values anymore with the new component
            // It will automatically display tags for array values when isMultiSelect is true
            if (!this.isMultiSelect(index)) {
              this.$refs[ref][0].setValue(item.value);
            }
          }
        }
      })
    }
  },
  data() {
    return {
      name: "",
      description: "",
      percentage: undefined,
      conditions: [{}],
      conditionTypes: [],
      errors: [],
    }
  },
  computed: {
    audienceOptions() {
      const audienceOptions = this.$store.getters.audienceOptions
      const audienceOption = audienceOptions.find(item => item.key == "audience")
      if (audienceOption) audienceOption.options = this.$store.getters.audiences.filter(item => item.id != (this.audience ? this.audience.id : undefined)).sort(function (a,b){
        const aField = a.name ? a.name.toUpperCase() : "";
        const bField = b.name ? b.name.toUpperCase() : "";
        if (aField > bField){
          return 1
        }else if (aField < bField){
          return -1
        }else{
          return 0
        }
      })
      return audienceOptions
    },
    keys() {
      return this.audienceOptions.map(item => item.key)
    },
    /** Safe access to country groups/clusters (handles failed import or different module shapes). */
    countryGroupsClustersSafe() {
      const raw = countryGroupsClusters
      const data = raw && (raw.default || raw)
      return data && Array.isArray(data.countryGroups) && Array.isArray(data.countryClusters)
        ? data
        : { countryGroups: [], countryClusters: [] }
    }
  },
  methods: {
    isMultiSelect(index) {
      return this.conditions[index].operator === 'in';
    },
    /**
     * Whether this condition is country/device_region with operator "in" (multi-select).
     * @param {number} index - Condition index
     * @returns {boolean}
     */
    isCountryInCondition(index) {
      const c = this.conditions[index]
      return c && (c.key === 'country' || c.key === 'device_region') && c.operator === 'in'
    },
    /**
     * Applies selected country group or cluster to condition value (comma-separated codes).
     * @param {Event} event - Change event from select
     * @param {number} index - Condition index
     */
    applyCountryShortcut(event, index) {
      const value = event.target && event.target.value
      if (!value) return
      const parts = String(value).split(':')
      const type = parts[0]
      const id = parts[1]
      const list = type === 'group' ? this.countryGroupsClustersSafe.countryGroups : this.countryGroupsClustersSafe.countryClusters
      if (!list) return
      const item = list.find(l => l.id === id)
      if (!item || !Array.isArray(item.countries)) return
      const newValue = item.countries.join(',')
      const updated = { ...this.conditions[index], value: newValue, countryShortcut: value }
      this.conditions.splice(index, 1, updated)
    },
    /**
     * Clears countryShortcut when value is manually edited.
     * @param {number} index - Condition index
     */
    onCountryValueInput(index) {
      const c = this.conditions[index]
      if (c && c.countryShortcut) {
        this.$set(this.conditions[index], 'countryShortcut', '')
      }
    },
    operatorChanged(index) {
      // When operator changes, reset the value
      if (this.isMultiSelect(index)) {
        // Initialize an empty string (not array) for multi-select with EnhancedAutocomplete
        this.conditions[index].value = '';
        if ((this.conditions[index].key === 'country' || this.conditions[index].key === 'device_region') && this.conditions[index].countryShortcut) {
          this.$set(this.conditions[index], 'countryShortcut', '')
        }
      } else if (Array.isArray(this.conditions[index].value)) {
        // Convert array to string if switching from multi-select to single select
        this.conditions[index].value = this.conditions[index].value.length > 0 
          ? this.conditions[index].value[0] 
          : undefined;
      }
      else if(this.conditions[index].key == "mobile_os_hardware_models" && this.conditions[index].operator == "in" && this.conditions[index].value.includes(';')){
        const values = this.conditions[index].value.split(';');
        this.conditions[index].value = values.length > 0 ? values[0].trim() : '';
      }
      else if (typeof this.conditions[index].value === 'string' && this.conditions[index].value.includes(',')) {
        // Handle comma-separated strings when switching from multi-select to single-select
        // Take just the first value
        const values = this.conditions[index].value.split(',');
        this.conditions[index].value = values.length > 0 ? values[0].trim() : '';
      }
    },
    addCondition() {
      this.conditions.push({})
    },
    search(index, type) {
      const backendUrl = this.$store.getters.backendUrl
      const httpService = new BaseHttpService(backendUrl)
      return async input => {
        if (input.length < 1) { 
          return [] 
        }else {
          try {
            const res = await httpService.get(`/autocomplete/${type}`, {str: input})
            if(res.data.length == 0) {
              let result = []
              result.push(input)
              return result
            }
            res.data.push(input)
            return res.data
          } catch (error) {
            console.error('Error during autocomplete search:', error);
            return []
          }
        }
      }
    },
    keySelected(index){
      const type = this.audienceOptions.find(item => item.key == this.conditions[index].key).type
      this.conditions[index].value = undefined
      if (this.conditions[index].countryShortcut) {
        this.$set(this.conditions[index], 'countryShortcut', '')
      }
      this.conditionTypes[index] = type
    },
    operatorTypes(type,conditionKey) {
      if(conditionKey && conditionKey.includes("marketing")){
          return ["is", "not", "contains"]
      }
      if(conditionKey && (conditionKey.includes("device_region") || conditionKey.includes("country"))){
          return ["is", "in", "not"]
      }
      else if (type == "autocomplete"){
        return ["is", "not", "in"]
      }
      else if (type == "codedSelect" || type == "select" || type == "objselect"){
        return ["is", "not"]
      }else if (type == "operators" || type == "calendar" || type == "custom_number"){
        return ["<", "<=", "==", ">", ">=", "!="];
      }else if (type == "list"){
        return ["in", "not"]
      }else if (type == "custom"){
        return ["in", "not", "contains"]
      }
    },
    valueOptions(key){
      const audienceItem = this.audienceOptions.find(item => item.key == key)
      if (audienceItem) return audienceItem.options
      else return []
    },
    addOrCondition(index){
      const newOrCondition = {}
      if(this.conditions[index].hasOr) {
        newOrCondition.hasOr = index + 2
      }
      this.conditions[index].hasOr = index+1
      this.conditions.splice(index+1,0,newOrCondition)
      this.conditionTypes.splice(index+1,0,{})
      for (let i=index+2;i<this.conditions.length;i++){
        const condition = this.conditions[i]
        if (condition.hasOr) condition.hasOr++
      }
    },
    removeCondition(index){
      const condition = this.conditions[index]
      const relatedConditions = this.conditions.filter(c => c.hasOr == index)
      
      if (relatedConditions.length > 0){
        if (condition.hasOr != undefined){
          relatedConditions.forEach(item => item.hasOr = condition.hasOr)
        }else {
          relatedConditions.forEach(item => delete item.hasOr)
        }
      }

      for (let i=0;i<this.conditions.length; i++){
        if (this.conditions[i].hasOr && this.conditions[i].hasOr > index){
          this.conditions[i].hasOr--
        }
      }

      this.conditions.splice(index,1)
      this.conditionTypes.splice(index,1)
    },
    createAudience(){
      // convert datetime to integer
      const conditions = JSON.parse(JSON.stringify(this.conditions))
      conditions.forEach(item => {
        if (item.key == 'created_at' || item.key == 'update_time'){
          item.value = new Date(item.value)*1
        }
      })
      this.errors = [];
      conditions.forEach(condition => {
        if(!condition.key){
          this.errors.push('Condition key required!');
        }
        if(!condition.operator){
          this.errors.push("Condition operator required!")
        }
        if(!condition.value || (Array.isArray(condition.value) && condition.value.length === 0)){
          this.errors.push("Condition value required!")
        }
      })
        
      if (!this.name) {
        this.errors.push('Name required!');
      }
      if (!this.description) {
        this.errors.push('Description required!');
      }
      if (!this.percentage) {
        this.errors.push('Percentage required!');
      }

      if (this.errors.length === 0) {
        this.$store.dispatch(
        'configAdd',
        {
          type: 'audience',
          payload: {
            name: this.name,
            description: this.description,
            percentage: this.percentage,
            conditions,
            created_at: Date.now()
          }
        } 
      )
        document.getElementById('close').click(); 
      }
    },
    editAudience(){
      // convert datetime to integer
      const conditions = JSON.parse(JSON.stringify(this.conditions))
      conditions.forEach(item => {
        if (item.key == 'created_at' || item.key == 'update_time'){
          item.value = new Date(item.value)*1
        }
      })
      // check if all conditions are set up correctly
      let conditionsAreGoodToGo = true
      conditions.forEach((item, index) => {
        if (item.key == undefined || item.operator == undefined || 
            item.value == undefined || item.value == '' || 
            (Array.isArray(item.value) && item.value.length === 0)) {
          conditionsAreGoodToGo = false
        }
      })
      if (conditionsAreGoodToGo){
        this.$store.dispatch(
          'configEdit', 
          {
            type: 'audience',
            payload: {
              id: this.audience.id,
              data: {
                name: this.name,
                description: this.description,
                percentage: this.percentage,
                conditions,
                created_at: this.audience.created_at
              }
            }
          }
        ).then(res => {
          this.$emit('edit-successful')
        }).catch(err => {
          this.$emit('edit-fail')
        })
      }else{
        alert(`There is something wrong with conditions`)
        this.$emit('edit-fail')
      }
    }
  },

  watch: {
    conditions: {
      handler() {
        // Deep watch for reactivity
      },
      deep: true
    }
  },
  components: {
    Autocomplete,
    Datetime,
    EnhancedAutocomplete
  }
}
</script>

<style scoped>
  input, select {
    color: #000 !important;
  }

  p {
    color: #525f7f;
  }

  .conditionDiv {
    color: #000 !important;
    background-color: #ecf0f1;
    border-radius: 10px;
  }
  
  .or-hr {
    background: #ecf0f1;
    margin-top: -10px;
    margin-bottom: -20px;
  }
</style>
